import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  ConfirmationDialog,
  Input,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@roger/ui-components';
import {
  AlertTriangle,
  Building2,
  CheckCircle2,
  Info,
  ListChecks,
  Loader2,
  Receipt,
  RefreshCw,
  Search,
  Send,
  type LucideIcon,
} from 'lucide-react';
import { Field, DropdownField } from '../FormFields';
import { ClinicSelect } from '../clinic/ClinicSelect';
import type {
  AgencyClinicResponse,
  BillType,
  BillingFrequency,
  ChargeType,
  PayorSearchResult,
  PayorType,
  TypeOfBill,
} from '../../services/clinicsApi';
import { PAYOR_TYPE_OPTIONS, searchPayors } from '../../services/clinicsApi';
import {
  describePayerLink,
  isMedicarePayorType,
  type PayerLinkTone,
  type SavedPayerLink,
} from './insuranceUtils';
import { useDebounce } from '../../hooks/useDebounce';
import InsuranceRatesPanel from './InsuranceRatesPanel';
import {
  BILL_TYPE_OPTIONS,
  BILLING_FREQUENCY_OPTIONS,
  DEFAULT_BILL_TYPE,
  effectiveBillType,
  isInvoiceBilled,
  INSURANCE_CHARGE_TYPE_OPTIONS,
  NO_READINESS_SKIPS,
  READINESS_SKIP_SWITCHES,
  TYPE_OF_BILL_DEFAULT_OPTION_VALUE,
  TYPE_OF_BILL_OPTIONS,
  type ReadinessSkipFlags,
} from '../../constants/insuranceBilling';

export type InsuranceModalTab = 'details' | 'rates';

// Searching a payer name sooner than this many characters lists most of the catalog.
const MIN_PAYOR_SEARCH_LENGTH = 2;
const PAYOR_SEARCH_DEBOUNCE_MS = 300;

export type InsuranceForm = ReadinessSkipFlags & {
  agency_clinic_id: string;
  name: string;
  edi_payer_id: string;
  edi_clearinghouse: string;
  visit_authorization_required: boolean;
  noa_required: boolean;
  /** null follows the payer's rule; true or false overrides it. */
  noa_omit_admitting_diagnosis: boolean | null;
  include_location_codes_on_noa: boolean | null;
  /** The linked payer's own rules, shown beside the override. */
  payor_noa_omit_admitting_diagnosis: boolean | null;
  payor_noa_include_location_codes: boolean | null;
  bill_type: BillType;
  payor_type: PayorType | null;
  billing_frequency: BillingFrequency;
  pdgm_enabled: boolean;
  charge_type: ChargeType;
  type_of_bill: TypeOfBill | null;
  do_not_multiply_charges_by_units: boolean;
  include_non_covered_visits_on_claims: boolean;
  health_plan_id: string;
  provider_code: string;
  provider_number: string;
  address_one: string;
  address_two: string;
  city: string;
  state: string;
  zip_code: string;
};

type InsuranceModalProps = {
  open: boolean;
  mode: 'create' | 'edit';
  form: InsuranceForm;
  onChange: (updates: Partial<InsuranceForm>) => void;
  onSave: () => void;
  onCancel: () => void;
  clinics: AgencyClinicResponse[];
  saving: boolean;
  // When true, the WellSky sync still owns this insurance (see
  // isWellSkySyncedInsurance): the modal is view-only because clinic-info sync
  // overwrites these fields.
  wellSkySynced?: boolean;
  // The persisted insurance being edited; null in the create flow. The
  // Billing rates tab needs it (rate rows attach to an insurance id), so the
  // tab is disabled until the insurance is saved. Rates stay editable even
  // for WellSky-synced insurances (unlike the details form), so the tab is
  // intentionally not gated by isViewOnly.
  insuranceId?: string | null;
  // Tab the modal opens on; a table-row rates action deep-links to 'rates'.
  initialTab?: InsuranceModalTab;
  // Billing config only exists for agencies using Roger EMR: the billing
  // section (billing frequency, charge type, health plan ID, PDGM,
  // do-not-multiply) and the Rates tab are hidden for everyone else, so an
  // intake-only agency sees the insurance info without them and those fields
  // keep their form defaults. Type of bill follows the same gate (EMR-880
  // review). Where it IS shown, ADDING an insurance must pick one: FL 4 is
  // per-insurance and an agency has to decide it, so a new row should not be
  // created with the column left NULL. Editing keeps the "Default" escape
  // hatch — existing rows predate the requirement and a WellSky-synced row may
  // legitimately have no scheme on file.
  isRogerEmrEnabled?: boolean;
  /**
   * Show only the tab it opened on, with no way to switch: Edit opens the
   * details, Rates opens the rates, each at its own fixed size.
   */
  singleTab?: boolean;
  /**
   * Given, an edit of a Roger-managed insurance shows Delete at the bottom
   * left: rare and for good, so one step in rather than on every row.
   */
  onDelete?: () => void;
  /** The saved payer link, so the form can say whether claims can use it. */
  savedPayerLink?: SavedPayerLink | null;
  /**
   * Show the Bill type select. Behind the invoice-billing flag
   * until the invoice screens ship. Roger-native, so editable on synced rows.
   */
  showBillType?: boolean;
  /**
   * The insurance has a claim past draft, so its bill type and non-covered
   * reporting are read-only here and the API refuses a change.
   */
  billingSettingsLocked?: boolean;
};

const BILLING_SETTINGS_LOCKED_REASON =
  'Locked: this insurance has a claim past draft or with a transmission. A change would leave the claim and the insurance disagreeing.';

const PAYER_LINK_STYLES: Record<
  PayerLinkTone,
  { icon: LucideIcon; className: string }
> = {
  linked: { icon: CheckCircle2, className: 'text-green-700' },
  unlinked: { icon: AlertTriangle, className: 'text-amber-700' },
  pending: { icon: Info, className: 'text-muted-foreground' },
};

// Forced margins: the form's even field spacing would otherwise win, and a
// heading as close as any field does not read as the start of a section.
const SectionHeader: React.FC<{
  icon: LucideIcon;
  children: React.ReactNode;
}> = ({ icon: Icon, children }) => (
  <h3 className='-mx-2 !mt-10 !mb-1 flex items-center gap-2 pb-1.5 text-base font-semibold text-foreground first:!mt-1'>
    <Icon className='h-4 w-4 text-muted-foreground' />
    {children}
  </h3>
);

const SwitchRow: React.FC<{
  id: string;
  label: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
}> = ({ id, label, checked, onCheckedChange, disabled }) => (
  <div className='flex items-center gap-3'>
    <Switch
      id={id}
      checked={checked}
      onCheckedChange={onCheckedChange}
      disabled={disabled}
    />
    <label
      htmlFor={id}
      className={`text-sm font-medium ${
        disabled ? 'text-muted-foreground' : 'cursor-pointer'
      }`}
    >
      {label}
    </label>
  </div>
);

const FOLLOW_PAYER = 'follow';

/**
 * A NOA rule the payer sets and this insurance may override. "Follow payer"
 * stores null, so a later change to the payer's rule still reaches it.
 */
const NoaRuleField: React.FC<{
  id: string;
  label: string;
  onLabel: string;
  offLabel: string;
  value: boolean | null;
  payorValue: boolean | null;
  onChange: (value: boolean | null) => void;
}> = ({ id, label, onLabel, offLabel, value, payorValue, onChange }) => (
  <DropdownField
    label={label}
    htmlFor={id}
    variant='formSelect'
    options={[
      {
        value: FOLLOW_PAYER,
        label: `Follow payer (${payorValue ? onLabel : offLabel})`,
      },
      { value: 'on', label: onLabel },
      { value: 'off', label: offLabel },
    ]}
    value={value === null ? FOLLOW_PAYER : value ? 'on' : 'off'}
    onChange={next => onChange(next === FOLLOW_PAYER ? null : next === 'on')}
  />
);

const PayerLinkStatus: React.FC<{ tone: PayerLinkTone; text: string }> = ({
  tone,
  text,
}) => {
  const { icon: Icon, className } = PAYER_LINK_STYLES[tone];
  return (
    <p
      className={`flex items-start gap-1.5 text-xs ${className}`}
      data-testid='payer-link-status'
      data-tone={tone}
    >
      <Icon className='mt-px h-3.5 w-3.5 shrink-0' />
      <span>{text}</span>
    </p>
  );
};

const InsuranceModal: React.FC<InsuranceModalProps> = ({
  open,
  mode,
  form,
  onChange,
  onSave,
  onCancel,
  clinics,
  saving,
  wellSkySynced = false,
  insuranceId = null,
  initialTab = 'details',
  isRogerEmrEnabled = false,
  singleTab = false,
  onDelete,
  savedPayerLink = null,
  showBillType = false,
  billingSettingsLocked = false,
}) => {
  const [payorSearchQuery, setPayorSearchQuery] = useState('');
  const [payorSearchResults, setPayorSearchResults] = useState<
    PayorSearchResult[]
  >([]);
  const [isSearchingPayors, setIsSearchingPayors] = useState(false);
  const [showPayorResults, setShowPayorResults] = useState(false);
  const [pickedPayor, setPickedPayor] = useState<PayorSearchResult | null>(
    null
  );
  // Only the newest search may write results: an older, slower answer would
  // otherwise replace the list for what the user typed last.
  const latestPayorSearchRef = useRef(0);

  const runPayorSearch = async (query: string) => {
    const trimmed = query.trim();
    if (!trimmed) return;
    const searchId = ++latestPayorSearchRef.current;
    setIsSearchingPayors(true);
    setShowPayorResults(true);
    try {
      const results = await searchPayors(trimmed);
      if (searchId === latestPayorSearchRef.current) {
        setPayorSearchResults(results);
      }
    } catch (error) {
      console.error('Failed to search payors:', error);
      if (searchId === latestPayorSearchRef.current) {
        setPayorSearchResults([]);
      }
    } finally {
      if (searchId === latestPayorSearchRef.current) {
        setIsSearchingPayors(false);
      }
    }
  };
  const debouncedPayorSearch = useDebounce(
    runPayorSearch,
    PAYOR_SEARCH_DEBOUNCE_MS
  );

  const clearPayorSearch = () => {
    debouncedPayorSearch.cancel();
    latestPayorSearchRef.current += 1;
    setPayorSearchResults([]);
    setShowPayorResults(false);
    setIsSearchingPayors(false);
  };

  const handlePayorQueryChange = (query: string) => {
    setPayorSearchQuery(query);
    if (query.trim().length >= MIN_PAYOR_SEARCH_LENGTH) {
      debouncedPayorSearch(query);
    } else {
      clearPayorSearch();
    }
  };

  // Enter and the button search at once, without waiting out the debounce.
  const handlePayorSearch = () => {
    debouncedPayorSearch.cancel();
    void runPayorSearch(payorSearchQuery);
  };

  // A payer with no Payer ID cannot be picked: the save links by Payer ID, so
  // it would clear the insurance's link instead of setting one.
  const handleSelectPayor = (payor: PayorSearchResult) => {
    if (!payor.electronic_payor_id) return;
    onChange({
      name: payor.payor_name,
      edi_payer_id: payor.electronic_payor_id,
      payor_noa_omit_admitting_diagnosis:
        payor.noa_omit_admitting_diagnosis ?? null,
      payor_noa_include_location_codes:
        payor.noa_include_location_codes ?? null,
    });
    setPickedPayor(payor);
    setPayorSearchQuery('');
    clearPayorSearch();
  };

  // Reset payer-lookup state when the modal closes so a new create/edit
  // session never shows the previous search query or results.
  useEffect(() => {
    if (!open) {
      setPayorSearchQuery('');
      setPickedPayor(null);
      clearPayorSearch();
    }
  }, [open]);

  // Billing rates live on their own tab (leadership: primary details stay on
  // the main tab, rates get a dedicated one). The tab needs a persisted
  // insurance to attach rows to.
  const canEditRates = mode === 'edit' && !!insuranceId;
  const showRatesTab = isRogerEmrEnabled;
  const [activeTab, setActiveTab] = useState<InsuranceModalTab>('details');
  // The rates panel mounts lazily on the first visit to its tab (so a
  // Details-only edit never fetches the rates grid or surfaces its load
  // toasts), then stays mounted so in-progress rate edits survive tab
  // switches.
  const [ratesTabVisited, setRatesTabVisited] = useState(false);
  const [ratesCount, setRatesCount] = useState<number | null>(null);
  const [ratesDirty, setRatesDirty] = useState(false);
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);

  // The NOA switches, readiness skips, bill type and non-covered visits on claims
  // stay editable on synced rows: they are Roger-native and the sync never writes them. Save on a
  // view-only modal enables only after one of them actually changes.
  const [rogerSettingsDirty, setRogerSettingsDirty] = useState(false);
  useEffect(() => {
    if (open) setRogerSettingsDirty(false);
  }, [open]);

  // Each open starts fresh on the requested tab (deep-links land on 'rates').
  // Before paint, so the dialog opens at that tab's width instead of drawing
  // at the details width and then jumping wide.
  useLayoutEffect(() => {
    if (open) {
      const startOnRates =
        initialTab === 'rates' && showRatesTab && canEditRates;
      setActiveTab(startOnRates ? 'rates' : 'details');
      setRatesTabVisited(startOnRates);
      setRatesCount(null);
      setRatesDirty(false);
    }
  }, [open, initialTab, showRatesTab, canEditRates]);

  // Closing with unsaved rate edits needs a confirm — the rates tab keeps its
  // own state and would otherwise be discarded silently.
  const requestClose = () => {
    if (ratesDirty) {
      setConfirmDiscardOpen(true);
    } else {
      onCancel();
    }
  };

  // A successful details save also closes the modal (the parent's success
  // path), so it is a close for the rates tab's purposes: saving with dirty
  // rate edits needs the same explicit confirm, not a silent discard.
  const [confirmSaveDiscardOpen, setConfirmSaveDiscardOpen] = useState(false);
  const requestSave = () => {
    if (ratesDirty) {
      setConfirmSaveDiscardOpen(true);
    } else {
      onSave();
    }
  };

  const isViewOnly = wellSkySynced;
  const isTraditionalMedicare = form.payor_type === 'MEDICARE_TRADITIONAL';
  const billType = effectiveBillType(form.bill_type, form.payor_type);

  // The Type of bill input lives inside the billing-config section, which is
  // Roger-EMR only.
  const showTypeOfBill = isRogerEmrEnabled;

  // Only enforced where the field is actually on screen — requiring it while
  // hidden would leave no way to satisfy the form.
  const mustPickTypeOfBill = mode === 'create' && showTypeOfBill;

  const isSaveDisabled =
    isViewOnly ||
    saving ||
    !form.name.trim() ||
    (mode === 'create' && (!form.agency_clinic_id || !form.payor_type)) ||
    (mustPickTypeOfBill && !form.type_of_bill);

  return (
    <>
      <Dialog open={open} onOpenChange={isOpen => !isOpen && requestClose()}>
        <DialogContent
          // The details form reads best narrow; the rates grid needs room. The
          // width follows the active tab so the modal "opens up" for the grid.
          className={`max-h-[85vh] flex flex-col ${
            activeTab === 'rates'
              ? // Always full height, so the grid does not grow and shrink
                // with the number of rates.
                'h-[85vh] w-[96vw] sm:max-w-[min(1200px,96vw)]'
              : 'sm:max-w-[760px]'
          }`}
        >
          <DialogHeader>
            {/* Named for the insurance and what is open on it, not a generic
                "Edit Insurance". */}
            <DialogTitle>
              {mode === 'create'
                ? 'Add Insurance'
                : form.name.trim() || 'Insurance'}
            </DialogTitle>
            <DialogDescription>
              {mode === 'create'
                ? 'Create a new insurance provider for a clinic.'
                : activeTab === 'rates'
                  ? 'Billing rates'
                  : isViewOnly
                    ? 'Synced from WellSky, so most settings are managed by the clinic-info sync'
                    : 'Payer, billing, NOA and authorization settings'}
            </DialogDescription>
          </DialogHeader>

          <Tabs
            value={activeTab}
            onValueChange={v => {
              setActiveTab(v as InsuranceModalTab);
              if (v === 'rates') setRatesTabVisited(true);
            }}
            className='flex min-h-0 flex-1 flex-col'
          >
            {showRatesTab && !singleTab && (
              <TabsList className='w-fit'>
                <TabsTrigger value='details'>Details</TabsTrigger>
                <TabsTrigger
                  value='rates'
                  disabled={!canEditRates}
                  title={
                    !canEditRates ? 'Save the insurance first.' : undefined
                  }
                >
                  Billing rates{ratesCount != null ? ` (${ratesCount})` : ''}
                </TabsTrigger>
              </TabsList>
            )}

            <TabsContent
              value='details'
              className='flex min-h-0 flex-1 flex-col data-[state=inactive]:hidden'
            >
              <div className='overflow-y-auto flex-1 px-2 pb-4 space-y-3 [&_input]:border-gray-300 [&_[role=combobox]]:border-gray-300'>
                {isViewOnly && (
                  <div className='flex items-start gap-2 rounded-md border border-border bg-muted/50 p-3 text-xs text-muted-foreground'>
                    <RefreshCw className='h-4 w-4 shrink-0 mt-0.5' />
                    <span>
                      Synced from WellSky. Clinic-info sync keeps this record
                      aligned with Kinnser
                      {isRogerEmrEnabled
                        ? ', so only the NOA and readiness settings are editable here.'
                        : '. All fields are read-only here.'}
                    </span>
                  </div>
                )}

                {/* Insurance details */}
                <SectionHeader icon={Building2}>
                  Insurance details
                </SectionHeader>

                {mode === 'create' && (
                  <ClinicSelect
                    label='Clinic'
                    clinics={clinics}
                    required
                    value={form.agency_clinic_id}
                    onChange={e =>
                      onChange({ agency_clinic_id: e.target.value })
                    }
                    placeholder='Select a clinic'
                  />
                )}

                <div className='grid gap-x-4 gap-y-3 sm:grid-cols-2'>
                  <Field
                    label='Insurance name'
                    htmlFor='ins-name'
                    required
                    value={form.name}
                    onChange={value => onChange({ name: value })}
                    copyable={false}
                    inputProps={{ disabled: isViewOnly }}
                  />
                  <DropdownField
                    label='Payer type'
                    htmlFor='ins-payor-type'
                    required={mode === 'create'}
                    variant='formSelect'
                    options={PAYOR_TYPE_OPTIONS}
                    value={form.payor_type ?? ''}
                    onChange={value => {
                      const pt = value ? (value as PayorType) : null;
                      // PDGM can be enabled on any payer type; picking a Medicare
                      // type defaults it On (Traditional Medicare requires it).
                      onChange({
                        payor_type: pt,
                        ...(pt === 'MEDICARE_TRADITIONAL'
                          ? NO_READINESS_SKIPS
                          : {}),
                        pdgm_enabled: isMedicarePayorType(pt)
                          ? true
                          : form.pdgm_enabled,
                        ...(pt === 'MEDICARE_TRADITIONAL'
                          ? { bill_type: DEFAULT_BILL_TYPE }
                          : {}),
                      });
                    }}
                    placeholder='—'
                    disabled={isViewOnly}
                  />
                </div>

                {/* Stedi payer-database lookup — autofills Insurance name + Payer ID.
              Hidden for WellSky-synced rows since the modal is view-only. */}
                {!isViewOnly && (
                  <div className='flex flex-col gap-1.5'>
                    <label className='text-sm font-medium'>
                      Lookup from payer database
                    </label>
                    <div className='flex gap-2'>
                      <Input
                        placeholder='Search by name or ID...'
                        value={payorSearchQuery}
                        onChange={e => handlePayorQueryChange(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handlePayorSearch();
                          }
                        }}
                        className='flex-1'
                      />
                      <Button
                        type='button'
                        variant='outline'
                        onClick={handlePayorSearch}
                        disabled={isSearchingPayors || !payorSearchQuery.trim()}
                      >
                        {isSearchingPayors ? (
                          <Loader2 className='h-4 w-4 animate-spin' />
                        ) : (
                          <Search className='h-4 w-4' />
                        )}
                      </Button>
                    </div>
                    {showPayorResults && (
                      <div className='max-h-[200px] overflow-y-auto rounded-md border'>
                        {payorSearchResults.length === 0 ? (
                          <div className='p-3 text-sm text-muted-foreground text-center'>
                            {isSearchingPayors
                              ? 'Searching...'
                              : 'No payors found'}
                          </div>
                        ) : (
                          payorSearchResults.map(payor => (
                            <button
                              key={payor.id}
                              type='button'
                              className='w-full text-left px-3 py-2 text-sm border-b last:border-b-0 enabled:cursor-pointer enabled:hover:bg-accent disabled:cursor-not-allowed disabled:opacity-60'
                              onClick={() => handleSelectPayor(payor)}
                              disabled={!payor.electronic_payor_id}
                            >
                              <div className='font-medium'>
                                {payor.payor_name}
                              </div>
                              <div className='text-muted-foreground text-xs'>
                                {payor.electronic_payor_id
                                  ? `Payer ID: ${payor.electronic_payor_id}`
                                  : "No Payer ID, so it can't be linked"}
                              </div>
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                )}

                <PayerLinkStatus
                  {...describePayerLink({
                    payerId: form.edi_payer_id,
                    pickedPayor,
                    savedPayerLink,
                    canPick: !isViewOnly,
                    billedByInvoice: isInvoiceBilled(billType),
                  })}
                />

                <div className='grid gap-x-4 gap-y-3 sm:grid-cols-2'>
                  <Field
                    label='Address line 1'
                    htmlFor='ins-address-one'
                    value={form.address_one}
                    onChange={value => onChange({ address_one: value })}
                    copyable={false}
                    inputProps={{ disabled: isViewOnly }}
                  />

                  <Field
                    label='Address line 2'
                    htmlFor='ins-address-two'
                    value={form.address_two}
                    onChange={value => onChange({ address_two: value })}
                    copyable={false}
                    inputProps={{ disabled: isViewOnly }}
                  />
                </div>

                <div className='grid grid-cols-3 gap-x-4'>
                  <div className='col-span-1'>
                    <Field
                      label='City'
                      htmlFor='ins-city'
                      value={form.city}
                      onChange={value => onChange({ city: value })}
                      copyable={false}
                      inputProps={{ disabled: isViewOnly }}
                    />
                  </div>
                  <div className='col-span-1'>
                    <Field
                      label='State'
                      htmlFor='ins-state'
                      value={form.state}
                      onChange={value => onChange({ state: value })}
                      copyable={false}
                      inputProps={{ maxLength: 2, disabled: isViewOnly }}
                    />
                  </div>
                  <div className='col-span-1'>
                    <Field
                      label='ZIP'
                      htmlFor='ins-zip'
                      value={form.zip_code}
                      onChange={value => onChange({ zip_code: value })}
                      copyable={false}
                      inputProps={{ maxLength: 10, disabled: isViewOnly }}
                    />
                  </div>
                </div>

                <div className='grid gap-x-4 gap-y-3 sm:grid-cols-2'>
                  <Field
                    label='Provider code'
                    htmlFor='ins-provider-code'
                    value={form.provider_code}
                    onChange={value => onChange({ provider_code: value })}
                    copyable={false}
                    inputProps={{ disabled: isViewOnly }}
                  />

                  <Field
                    label='Provider number'
                    htmlFor='ins-provider-number'
                    value={form.provider_number}
                    onChange={value => onChange({ provider_number: value })}
                    copyable={false}
                    inputProps={{ disabled: isViewOnly }}
                  />
                </div>

                {/* Billing config — Roger EMR agencies only */}
                {isRogerEmrEnabled && (
                  <>
                    <SectionHeader icon={Receipt}>Billing</SectionHeader>

                    <div className='grid gap-x-4 gap-y-3 sm:grid-cols-2'>
                      <DropdownField
                        label='Billing frequency'
                        htmlFor='ins-billing-frequency'
                        variant='formSelect'
                        options={BILLING_FREQUENCY_OPTIONS}
                        value={form.billing_frequency}
                        onChange={value =>
                          onChange({
                            billing_frequency: value as BillingFrequency,
                          })
                        }
                        disabled={isViewOnly}
                      />

                      <DropdownField
                        label='Charge type'
                        htmlFor='ins-charge-type'
                        variant='formSelect'
                        options={INSURANCE_CHARGE_TYPE_OPTIONS}
                        value={form.charge_type}
                        onChange={value =>
                          onChange({ charge_type: value as ChargeType })
                        }
                        disabled={isViewOnly}
                      />
                    </div>

                    <div className='grid gap-x-4 gap-y-3 sm:grid-cols-2'>
                      {/* UB-04 FL 4 scheme (EMR-880), Roger-EMR agencies only.
                        Unset = default 329; the Default option maps to null so
                        a set value can be cleared again. When adding, that
                        option is dropped, so the only way forward is an
                        explicit choice. */}
                      {showTypeOfBill && (
                        <DropdownField
                          label='Type of bill'
                          htmlFor='ins-type-of-bill'
                          variant='formSelect'
                          required={mustPickTypeOfBill}
                          placeholder={
                            mustPickTypeOfBill
                              ? 'Select a type of bill'
                              : 'Select an option'
                          }
                          options={
                            mustPickTypeOfBill
                              ? TYPE_OF_BILL_OPTIONS.filter(
                                  option =>
                                    option.value !==
                                    TYPE_OF_BILL_DEFAULT_OPTION_VALUE
                                )
                              : TYPE_OF_BILL_OPTIONS
                          }
                          value={
                            mustPickTypeOfBill
                              ? (form.type_of_bill ?? '')
                              : (form.type_of_bill ??
                                TYPE_OF_BILL_DEFAULT_OPTION_VALUE)
                          }
                          onChange={value =>
                            onChange({
                              type_of_bill:
                                value === TYPE_OF_BILL_DEFAULT_OPTION_VALUE
                                  ? null
                                  : (value as TypeOfBill),
                            })
                          }
                          disabled={isViewOnly}
                        />
                      )}

                      <Field
                        label='Health plan ID (UB-04 locator 51)'
                        htmlFor='ins-health-plan-id'
                        value={form.health_plan_id}
                        onChange={value => onChange({ health_plan_id: value })}
                        copyable={false}
                        inputProps={{ disabled: isViewOnly }}
                      />
                    </div>

                    <div className='grid gap-x-4 gap-y-3 pt-1 sm:grid-cols-2'>
                      {/* PDGM can be enabled on any payer type; Traditional
                        Medicare always requires it (backend forces it on). */}
                      <SwitchRow
                        id='ins-pdgm'
                        label='PDGM enabled'
                        checked={
                          form.payor_type === 'MEDICARE_TRADITIONAL'
                            ? true
                            : form.pdgm_enabled
                        }
                        onCheckedChange={checked =>
                          onChange({ pdgm_enabled: checked })
                        }
                        disabled={
                          isViewOnly ||
                          form.payor_type === 'MEDICARE_TRADITIONAL'
                        }
                      />

                      <SwitchRow
                        id='ins-no-multiply'
                        label='Do not multiply charges by units'
                        checked={form.do_not_multiply_charges_by_units}
                        onCheckedChange={checked =>
                          onChange({
                            do_not_multiply_charges_by_units: checked,
                          })
                        }
                        disabled={isViewOnly}
                      />

                      {/* Roger-native, so editable on WellSky-synced rows. */}
                      <SwitchRow
                        id='ins-non-covered-visits-on-claims'
                        label='Report non-covered visits on claims'
                        checked={form.include_non_covered_visits_on_claims}
                        disabled={billingSettingsLocked}
                        onCheckedChange={checked => {
                          setRogerSettingsDirty(true);
                          onChange({
                            include_non_covered_visits_on_claims: checked,
                          });
                        }}
                      />
                      <p className='-mt-2 pl-14 text-xs text-muted-foreground sm:col-span-2'>
                        When on, non-covered visits go on the claim as
                        non-covered lines (UB-04 FL 48). A change applies to
                        claims created afterwards; a claim already created keeps
                        the setting it was created with.
                      </p>
                      {billingSettingsLocked && (
                        <p
                          className='-mt-2 pl-14 text-xs text-muted-foreground sm:col-span-2'
                          data-testid='billing-settings-locked-reason'
                        >
                          {BILLING_SETTINGS_LOCKED_REASON}
                        </p>
                      )}
                    </div>
                  </>
                )}

                {/* EDI & authorization */}
                <SectionHeader icon={Send}>
                  EDI &amp; authorization
                </SectionHeader>

                <div className='grid gap-x-4 gap-y-3 sm:grid-cols-2'>
                  {showBillType && (
                    <div className='sm:col-span-2'>
                      <DropdownField
                        label='Bill type'
                        htmlFor='ins-bill-type'
                        variant='formSelect'
                        options={BILL_TYPE_OPTIONS}
                        value={billType}
                        onChange={value => {
                          setRogerSettingsDirty(true);
                          onChange({ bill_type: value as BillType });
                        }}
                        // Traditional Medicare is always billed by UB-04.
                        disabled={
                          isTraditionalMedicare || billingSettingsLocked
                        }
                      />
                      {billingSettingsLocked && (
                        <p className='mt-1.5 text-xs text-muted-foreground'>
                          {BILLING_SETTINGS_LOCKED_REASON}
                        </p>
                      )}
                      {isInvoiceBilled(billType) && form.noa_required && (
                        <p className='mt-1.5 flex items-start gap-1.5 text-xs text-amber-700'>
                          <AlertTriangle className='mt-0.5 h-3.5 w-3.5 shrink-0' />
                          An invoice payer usually takes no NOA. Check that this
                          insurance needs one.
                        </p>
                      )}
                    </div>
                  )}
                  <Field
                    label='Payer ID'
                    htmlFor='ins-payer-id'
                    value={form.edi_payer_id}
                    onChange={value =>
                      onChange({
                        edi_payer_id: value,
                        payor_noa_omit_admitting_diagnosis: null,
                        payor_noa_include_location_codes: null,
                      })
                    }
                    copyable={false}
                    inputProps={{ disabled: isViewOnly }}
                  />

                  <Field
                    label='Clearinghouse'
                    htmlFor='ins-clearinghouse'
                    value={form.edi_clearinghouse}
                    onChange={value => onChange({ edi_clearinghouse: value })}
                    copyable={false}
                    inputProps={{ disabled: isViewOnly }}
                  />
                </div>

                <div className='grid gap-x-4 gap-y-3 pt-1 sm:grid-cols-2'>
                  <SwitchRow
                    id='ins-auth-required'
                    label='Authorization required'
                    checked={form.visit_authorization_required}
                    onCheckedChange={checked =>
                      onChange({ visit_authorization_required: checked })
                    }
                    disabled={isViewOnly}
                  />

                  <SwitchRow
                    id='ins-noa-required'
                    label='NOA required'
                    checked={form.noa_required}
                    onCheckedChange={checked =>
                      onChange({ noa_required: checked })
                    }
                    disabled={isViewOnly}
                  />

                  {/* NOA settings are Roger-native (the WellSky sync never
                  writes them), so they stay editable on synced rows — but
                  they are EMR-only features, hidden from portal-only
                  agencies. */}
                  {isRogerEmrEnabled && (
                    <>
                      <NoaRuleField
                        id='ins-noa-omit-admitting-dx'
                        label='Admitting diagnosis on NOA'
                        offLabel='Send it'
                        onLabel='Omit it'
                        value={form.noa_omit_admitting_diagnosis}
                        payorValue={form.payor_noa_omit_admitting_diagnosis}
                        onChange={value => {
                          setRogerSettingsDirty(true);
                          onChange({ noa_omit_admitting_diagnosis: value });
                        }}
                      />
                      <NoaRuleField
                        id='ins-include-location-codes-on-noa'
                        label='Location codes on NOA (61 and 85)'
                        offLabel='Leave off'
                        onLabel='Include'
                        value={form.include_location_codes_on_noa}
                        payorValue={form.payor_noa_include_location_codes}
                        onChange={value => {
                          setRogerSettingsDirty(true);
                          onChange({ include_location_codes_on_noa: value });
                        }}
                      />
                    </>
                  )}
                </div>

                {/* Billing Manager readiness is EMR-only. Like the
                  NOA switches, the skips stay editable on synced rows. */}
                {isRogerEmrEnabled && (
                  <>
                    <SectionHeader icon={ListChecks}>
                      Readiness checks
                    </SectionHeader>
                    <p className='text-xs text-muted-foreground'>
                      {form.payor_type === 'MEDICARE_TRADITIONAL'
                        ? 'Traditional Medicare requires every check, including the signed CMS 485.'
                        : 'A skipped check is not required before Create Claim.'}
                    </p>
                    <div className='grid gap-x-4 gap-y-3 pt-1 sm:grid-cols-2'>
                      {READINESS_SKIP_SWITCHES.map(({ field, label }) => (
                        <SwitchRow
                          key={field}
                          id={`ins-${field}`}
                          label={label}
                          checked={form[field]}
                          disabled={form.payor_type === 'MEDICARE_TRADITIONAL'}
                          onCheckedChange={checked => {
                            setRogerSettingsDirty(true);
                            onChange({ [field]: checked });
                          }}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>

              <DialogFooter className='pt-4'>
                {mode === 'edit' && onDelete && !wellSkySynced && (
                  <Button
                    variant='destructive'
                    onClick={onDelete}
                    className='sm:mr-auto'
                  >
                    Retire insurance
                  </Button>
                )}
                <Button variant='outline' onClick={requestClose}>
                  {isViewOnly ? 'Close' : 'Cancel'}
                </Button>
                {/* A synced row without the EMR-only NOA switches on screen
                  has nothing savable, so the old Close-only footer stays. */}
                {(!isViewOnly || isRogerEmrEnabled) && (
                  <Button
                    onClick={requestSave}
                    disabled={
                      isViewOnly
                        ? !rogerSettingsDirty || saving
                        : isSaveDisabled
                    }
                  >
                    {saving && (
                      <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                    )}
                    {mode === 'create' ? 'Create Insurance' : 'Save Changes'}
                  </Button>
                )}
              </DialogFooter>
            </TabsContent>

            {/* Rendered only once the tab has been visited (lazy first
              mount), then forceMount keeps in-progress rate edits alive
              across tab switches (Radix unmounts inactive tabs by default). */}
            {showRatesTab && canEditRates && insuranceId && ratesTabVisited && (
              <TabsContent
                value='rates'
                forceMount
                className='flex min-h-0 flex-1 flex-col data-[state=inactive]:hidden'
              >
                <InsuranceRatesPanel
                  insuranceId={insuranceId}
                  wellSkySynced={wellSkySynced}
                  inheritedChargeType={form.charge_type}
                  onDirtyChange={setRatesDirty}
                  onRatesCountChange={setRatesCount}
                />
              </TabsContent>
            )}
          </Tabs>
        </DialogContent>
      </Dialog>

      <ConfirmationDialog
        open={confirmDiscardOpen}
        title='Discard unsaved rate changes?'
        message='Your rate edits have not been saved. Close without saving?'
        confirmLabel='Discard'
        cancelLabel='Keep editing'
        onAccept={() => {
          setConfirmDiscardOpen(false);
          onCancel();
        }}
        onCancel={() => setConfirmDiscardOpen(false)}
      />

      <ConfirmationDialog
        open={confirmSaveDiscardOpen}
        title='Discard unsaved rate changes?'
        message='Saving the insurance closes this window, and your rate edits on the Billing rates tab have not been saved. Save the insurance and discard the rate edits?'
        confirmLabel='Save and discard rate edits'
        cancelLabel='Keep editing'
        onAccept={() => {
          setConfirmSaveDiscardOpen(false);
          onSave();
        }}
        onCancel={() => setConfirmSaveDiscardOpen(false)}
      />
    </>
  );
};

export default InsuranceModal;
